import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Trash2, Plus, TrendingUp, Target, MailWarning } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UpsellTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestTone, setSuggestTone] = useState('freundlich-motivierend');
  const [suggestFocus, setSuggestFocus] = useState('Staffelrabatte');
  const [suggestions, setSuggestions] = useState([]);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [seasonDetected, setSeasonDetected] = useState(null);
  const [seasonOverride, setSeasonOverride] = useState('');
  const [useSeasonal, setUseSeasonal] = useState(true);

  const token = () => localStorage.getItem('token');

  const reload = async () => {
    try {
      const [tplsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/shop/upsell-templates`, { headers: { Authorization: `Bearer ${token()}` } }),
        axios.get(`${API}/admin/shop/upsell-stats`, { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      setTemplates(tplsRes.data.templates || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Saison-Kontext laden
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/shop/upsell-templates/season-context`, { headers: { Authorization: `Bearer ${token()}` } });
        setSeasonOptions(res.data.options || []);
        setSeasonDetected(res.data.detected);
      } catch (_) {}
    })();
  }, []);

  const saveTemplate = async (tpl) => {
    try {
      if (tpl.id) {
        await axios.put(`${API}/admin/shop/upsell-templates/${tpl.id}`, {
          name: tpl.name, title: tpl.title, body: tpl.body,
          weight: Number(tpl.weight) || 1, is_active: !!tpl.is_active, tone: tpl.tone || '',
        }, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success('Template aktualisiert');
      } else {
        await axios.post(`${API}/admin/shop/upsell-templates`, {
          name: tpl.name, title: tpl.title, body: tpl.body,
          weight: Number(tpl.weight) || 1, is_active: !!tpl.is_active, tone: tpl.tone || '',
        }, { headers: { Authorization: `Bearer ${token()}` } });
        toast.success('Template erstellt');
      }
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Template wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/admin/shop/upsell-templates/${id}`, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Template gelöscht');
      await reload();
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (tpl) => {
    await saveTemplate({ ...tpl, is_active: !tpl.is_active });
  };

  const askAi = async () => {
    try {
      setSuggestLoading(true);
      const payload = {
        tone: suggestTone,
        count: 4,
        focus: suggestFocus,
        seasonal: useSeasonal,
      };
      if (seasonOverride && seasonOverride !== 'none') {
        payload.season_override = seasonOverride;
      }
      const res = await axios.post(`${API}/admin/shop/upsell-templates/ai-suggest`, payload, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setSuggestions(res.data.suggestions || []);
      const sc = res.data.season_context?.primary?.label;
      toast.success(`${res.data.suggestions?.length || 0} KI-Vorschläge generiert${sc ? ' · Kontext: ' + sc : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Fehler');
    } finally {
      setSuggestLoading(false);
    }
  };

  const acceptSuggestion = async (s) => {
    await saveTemplate({
      name: s.name, title: s.title, body: s.body, tone: s.tone || '', weight: 3, is_active: true,
    });
    setSuggestions(suggestions.filter(x => x !== s));
  };

  const newTemplate = () => setEditing({ name: '', title: '', body: '', weight: 3, is_active: true, tone: '' });

  if (loading) return <p className="text-sm text-gray-500">Lädt...</p>;

  const totals = stats?.totals || {};
  const heatmap = stats?.heatmap || [];
  const maxOpens = Math.max(1, ...heatmap.map(h => h.opened));

  return (
    <div className="space-y-4" data-testid="upsell-templates-manager">
      {/* Totals Bar */}
      <div className="grid grid-cols-3 gap-2">
        <div className="border rounded-lg p-3 bg-white">
          <p className="text-xs text-gray-500">Gesendet</p>
          <p className="text-xl font-bold">{totals.sent || 0}</p>
        </div>
        <div className="border rounded-lg p-3 bg-white">
          <p className="text-xs text-gray-500">Öffnungsrate</p>
          <p className="text-xl font-bold text-blue-600">{(totals.open_rate || 0).toFixed(1)}%</p>
        </div>
        <div className="border rounded-lg p-3 bg-white">
          <p className="text-xs text-gray-500">Conversion (48h)</p>
          <p className="text-xl font-bold text-green-600">{(totals.conversion_rate || 0).toFixed(1)}%</p>
        </div>
      </div>

      {/* Template-Liste */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h5 className="font-semibold flex items-center gap-2">
            <Target className="w-4 h-4" /> A/B Templates
            <Badge variant="outline" className="ml-1">{templates.length}</Badge>
          </h5>
          <Button size="sm" onClick={newTemplate} data-testid="new-upsell-template-btn">
            <Plus className="w-4 h-4 mr-1" /> Neu
          </Button>
        </div>

        {templates.length === 0 && !editing && (
          <p className="text-sm text-gray-500 italic">Noch keine Templates. Lege unten welche an oder lass sie dir von der KI vorschlagen.</p>
        )}

        {templates.map(tpl => {
          const s = tpl.stats || {};
          const sent = s.sent || 0;
          const isWinner = sent >= 50 && tpl.is_winner;
          return (
            <div key={tpl.id} className={`border rounded-lg p-3 ${tpl.is_active ? 'bg-white' : 'bg-gray-50 opacity-70'}`} data-testid={`upsell-template-${tpl.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{tpl.name}</span>
                    {tpl.tone && <Badge variant="outline" className="text-xs">{tpl.tone}</Badge>}
                    {!tpl.is_active && <Badge className="bg-gray-400 text-xs">Inaktiv</Badge>}
                    {isWinner && <Badge className="bg-amber-500 text-xs">🏆 Winner</Badge>}
                  </div>
                  <p className="text-sm font-medium mt-1">{tpl.title}</p>
                  <p className="text-sm text-gray-600">{tpl.body}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>Gesendet: <strong>{sent}</strong></span>
                    <span>Geöffnet: <strong className="text-blue-600">{s.opened || 0}</strong> ({tpl.open_rate || 0}%)</span>
                    <span>Conversion: <strong className="text-green-600">{s.converted || 0}</strong> ({tpl.conversion_rate || 0}%)</span>
                    <span>Gewicht: {tpl.weight}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...tpl })} data-testid={`edit-tpl-${tpl.id}`}>Bearbeiten</Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(tpl)}>
                    {tpl.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteTemplate(tpl.id)} className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Editor */}
      {editing && (
        <div className="border-2 border-amber-400 rounded-lg p-4 space-y-3 bg-amber-50" data-testid="upsell-template-editor">
          <h5 className="font-semibold">{editing.id ? 'Template bearbeiten' : 'Neues Template'}</h5>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">Name (intern)</label>
              <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="tpl-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Ton (z.B. Dringlich, Witz, FOMO)</label>
              <Input value={editing.tone || ''} onChange={e => setEditing({ ...editing, tone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Titel (max ~40 Zeichen)</label>
            <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="tpl-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Body (max ~150 Zeichen)</label>
            <textarea
              value={editing.body}
              onChange={e => setEditing({ ...editing, body: e.target.value })}
              rows={3}
              className="w-full border rounded-md px-3 py-2 text-sm"
              data-testid="tpl-body-input"
            />
          </div>
          <div className="flex items-center gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600">Gewicht (1-10)</label>
              <Input type="number" min="1" max="10" value={editing.weight || 1} onChange={e => setEditing({ ...editing, weight: e.target.value })} className="w-24" />
            </div>
            <label className="flex items-center gap-2 text-sm pt-4">
              <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
              Aktiv
            </label>
          </div>
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
            Platzhalter: <code>{'{first_name}'}</code>, <code>{'{extra_points}'}</code>, <code>{'{next_points}'}</code>, <code>{'{next_label}'}</code>, <code>{'{next_discount_percent}'}</code>, <code>{'{extra_cost}'}</code>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => saveTemplate(editing)} data-testid="save-tpl-btn">Speichern</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Abbrechen</Button>
          </div>
        </div>
      )}

      {/* AI Suggestions */}
      <div className="border rounded-lg p-4 space-y-3 bg-gradient-to-r from-purple-50 to-pink-50">
        <h5 className="font-semibold flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" /> KI-Vorschläge
        </h5>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Grundton</label>
            <Input value={suggestTone} onChange={e => setSuggestTone(e.target.value)} placeholder="freundlich-motivierend" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Fokus</label>
            <Input value={suggestFocus} onChange={e => setSuggestFocus(e.target.value)} placeholder="Staffelrabatte" />
          </div>
        </div>
        {/* Saisonaler Kontext */}
        <div className="border border-purple-200 rounded-lg p-3 bg-white/60 space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-purple-900">
            <input
              type="checkbox"
              checked={useSeasonal}
              onChange={e => setUseSeasonal(e.target.checked)}
              className="rounded"
              data-testid="ai-seasonal-toggle"
            />
            🌿 Saisonalen Kontext nutzen
            {seasonDetected?.primary?.label && (
              <Badge variant="outline" className="text-xs">
                Jetzt: {seasonDetected.primary.label}
              </Badge>
            )}
          </label>
          {useSeasonal && (
            <div>
              <label className="text-xs text-gray-600 block mb-1">Kontext manuell setzen (optional)</label>
              <select
                value={seasonOverride}
                onChange={e => setSeasonOverride(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="ai-season-override"
              >
                <option value="">🎯 Automatisch erkannt ({seasonDetected?.primary?.label || '…'})</option>
                {seasonOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <Button size="sm" onClick={askAi} disabled={suggestLoading} data-testid="ai-suggest-btn">
          {suggestLoading ? 'KI denkt nach...' : '✨ 4 Vorschläge generieren'}
        </Button>
        {suggestions.length > 0 && (
          <div className="space-y-2 mt-2">
            {suggestions.map((s, i) => (
              <div key={i} className="border rounded-lg p-3 bg-white" data-testid={`ai-suggestion-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{s.name}</span>
                  {s.tone && <Badge variant="outline" className="text-xs">{s.tone}</Badge>}
                </div>
                <p className="text-sm font-medium">{s.title}</p>
                <p className="text-sm text-gray-600">{s.body}</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => acceptSuggestion(s)} data-testid={`accept-suggestion-${i}`}>
                  Als Template übernehmen
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Heatmap */}
      {heatmap.length > 0 && (
        <div className="border rounded-lg p-4">
          <h5 className="font-semibold flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4" /> Heatmap: Beste Öffnungszeit (letzte 60 Tage)
          </h5>
          <div className="grid grid-cols-12 gap-1">
            {Array.from({ length: 24 }).map((_, hour) => {
              const h = heatmap.find(x => x.hour === hour);
              const opens = h?.opened || 0;
              const intensity = maxOpens > 0 ? opens / maxOpens : 0;
              const bg = `rgba(234, 179, 8, ${intensity})`;
              return (
                <div key={hour} className="flex flex-col items-center" title={`${hour}:00 — ${opens} Öffnungen, Open-Rate ${h?.open_rate || 0}%`}>
                  <div style={{ backgroundColor: bg || '#f3f4f6' }} className="w-full h-10 rounded border border-gray-200 flex items-center justify-center text-xs font-medium">
                    {opens || ''}
                  </div>
                  <span className="text-[10px] text-gray-500 mt-0.5">{String(hour).padStart(2, '0')}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2">Je dunkler, desto mehr Öffnungen. Nutze die beste Stunde für deine Versand-Zeit.</p>
        </div>
      )}

      {/* Fallback-Hinweise */}
      <div className="border rounded-lg p-3 bg-blue-50 text-sm flex items-start gap-2">
        <MailWarning className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-blue-800">Fallback-Kanäle (konfigurierbar oben)</p>
          <p className="text-blue-700 text-xs">
            Wenn ein Kunde keine App installiert hat, wird die Nachricht per E-Mail verschickt. WhatsApp folgt zeitversetzt (Standard: 24 h später, nur wenn keine Conversion erfolgte).
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpsellTemplatesManager;
